import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Eye, Ear, Hand, Wind, Heart, CheckCircle2, Check } from "lucide-react";
import { useUniversalTracking } from '@/hooks/useUniversalTracking';
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';

interface GroundingExerciseProps {
  onComplete?: () => void;
}

type ExerciseStep = 'symptoms-before' | 'grounding' | 'symptoms-after' | 'complete';

export const GroundingExercise = ({ onComplete }: GroundingExerciseProps) => {
  const [step, setStep] = useState<ExerciseStep>('symptoms-before');
  const [currentGroundingStep, setCurrentGroundingStep] = useState(0);
  const [responses, setResponses] = useState<string[]>([]);
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const { logExercise, isLogging } = useUniversalTracking();

  const steps = [
    { 
      number: 5, 
      sense: 'See', 
      icon: Eye, 
      prompt: 'Look around and name 5 things you can see', 
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      examples: ['A clock on the wall', 'Your phone', 'A window', 'A plant', 'Your hands']
    },
    { 
      number: 4, 
      sense: 'Touch', 
      icon: Hand, 
      prompt: 'Notice 4 things you can physically feel', 
      color: 'text-soft-accent',
      bgColor: 'bg-soft-accent/10',
      examples: ['Your feet on the floor', 'Clothes on your skin', 'Temperature of the air', 'Texture of your chair']
    },
    { 
      number: 3, 
      sense: 'Hear', 
      icon: Ear, 
      prompt: 'Listen for 3 sounds you can hear', 
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      examples: ['Traffic outside', 'Your breathing', 'A fan or AC', 'Birds chirping']
    },
    { 
      number: 2, 
      sense: 'Smell', 
      icon: Wind, 
      prompt: 'Identify 2 things you can smell (or 2 smells you like)', 
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      examples: ['Coffee', 'Fresh air', 'Your soap', 'A candle']
    },
    { 
      number: 1, 
      sense: 'Taste', 
      icon: Heart, 
      prompt: 'Notice 1 thing you can taste', 
      color: 'text-soft-accent',
      bgColor: 'bg-soft-accent/10',
      examples: ['Toothpaste', 'Your last meal', 'Water', 'Your lips']
    }
  ];

  const currentStepData = steps[currentGroundingStep];
  const Icon = currentStepData.icon;
  const progress = ((currentGroundingStep + 1) / steps.length) * 100;

  const handleComplete = async () => {
    const success = await logExercise({
      exercise_type: 'grounding_5_4_3_2_1',
      exercise_name: '5-4-3-2-1 Grounding',
      category: 'anxiety',
      duration_minutes: 5,
      completion_data: {
        senses_completed: steps.length
      },
      symptoms_before: symptomsBefore,
      symptoms_after: symptomsAfter,
      xp_earned: 15
    });

    if (success) {
      setStep('complete');
    }
  };

  const handleNext = () => {
    if (currentGroundingStep < steps.length - 1) {
      setCurrentGroundingStep(currentGroundingStep + 1);
      setResponses([]);
    } else {
      setStep('symptoms-after');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {step === 'symptoms-before' && (
        <div className="space-y-4">
          <Card className="bg-gradient-therapeutic border-primary/20">
            <CardContent className="py-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Eye className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">5-4-3-2-1 Grounding</h3>
                    <p className="text-xs text-muted-foreground">Anchor yourself in the present moment</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <UniversalSymptomTracker
            mode="before"
            onRatingsChange={setSymptomsBefore}
            compact
          />
          <Button 
            onClick={() => setStep('grounding')} 
            className="w-full"
            disabled={Object.keys(symptomsBefore).length === 0}
          >
            Begin Exercise
          </Button>
        </div>
      )}

      {step === 'grounding' && (
        <>
          {/* Progress Header */}
          <Card className="bg-gradient-therapeutic border-primary/20">
            <CardContent className="py-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${currentStepData.bgColor}`}>
                      <Icon className={`h-5 w-5 ${currentStepData.color}`} />
                    </div>
                    <div>
                      <h3 className="font-medium">5-4-3-2-1 Grounding</h3>
                      <p className="text-xs text-muted-foreground">Step {currentGroundingStep + 1} of 5</p>
                    </div>
                  </div>
                  <Badge variant="secondary">{Math.round(progress)}%</Badge>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          {/* Main Exercise Card */}
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-3 rounded-full ${currentStepData.bgColor}`}>
                  <Icon className={`h-6 w-6 ${currentStepData.color}`} />
                </div>
                <div>
                  <CardTitle className="text-2xl font-light">
                    {currentStepData.number} Things You Can {currentStepData.sense}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Instructions */}
              <div className="bg-gradient-calm rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium">{currentStepData.prompt}</p>
                <p className="text-xs text-muted-foreground">
                  Take your time. Really notice each item. This helps bring you back to the present moment.
                </p>
              </div>

              {/* Examples */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Examples to inspire you:</p>
                <div className="grid gap-2">
                  {currentStepData.examples.map((example, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 text-sm bg-background/50 rounded-lg px-3 py-2 border border-border/30"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                      <span>{example}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Completion Checklist */}
              <div className="bg-background/50 rounded-lg p-4 space-y-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Check off as you identify each item:
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {Array.from({ length: currentStepData.number }).map((_, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-background/80 cursor-pointer transition-colors"
                      onClick={() => {
                        const newResponses = [...responses];
                        newResponses[idx] = 'checked';
                        setResponses(newResponses);
                      }}
                    >
                      {responses[idx] ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-border flex-shrink-0"></div>
                      )}
                      <span className="text-sm">Item {idx + 1}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {currentGroundingStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentGroundingStep(currentGroundingStep - 1);
                      setResponses([]);
                    }}
                    className="flex-1"
                  >
                    Previous Step
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className="flex-1"
                  disabled={responses.filter(r => r === 'checked').length < currentStepData.number}
                >
                  {currentGroundingStep === steps.length - 1 ? 'Rate How You Feel' : 'Next Step'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Educational Note */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">Why This Works</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  The 5-4-3-2-1 technique activates your senses to anchor you in the present moment. 
                  It interrupts anxious thoughts by engaging your observation skills, helping your nervous 
                  system recognise you're safe right now.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {step === 'symptoms-after' && (
        <div className="space-y-4">
          <UniversalSymptomTracker
            mode="after"
            previousRatings={symptomsBefore}
            onRatingsChange={setSymptomsAfter}
          />
          <Button 
            onClick={handleComplete} 
            className="w-full"
            disabled={isLogging || Object.keys(symptomsAfter).length === 0}
          >
            {isLogging ? 'Saving...' : 'Complete Exercise (+15 XP)'}
          </Button>
        </div>
      )}

      {step === 'complete' && (
        <Card className="bg-gradient-calm border-emerald-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <Check className="w-6 h-6" />
              Grounding Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-foreground/80">
              Well done. You've anchored yourself in the present moment. Your senses have helped 
              your nervous system recognise that you're safe right here, right now.
            </p>
            <Button onClick={() => onComplete?.()} className="w-full" size="lg">
              Done
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
